<template>
  <LayoutPage title=" Inventory Report" :loading="loading">
    <v-row v-if="showRegionFacilitySelectBoxes" no-gutters>
      <v-col cols="12" sm="8" md="6" lg="4">
        <RegionAndFacilitySelect class="mt-2" />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-skeleton-loader
          :loading="loading"
          type="table-thead, table-row-divider@7"
          class="black-border"
        >
          <v-data-table
            :headers="headers"
            :items="inventoryData"
            :sort-by.sync="tableState.sortBy"
            :sort-desc.sync="tableState.sortDesc"
            :page.sync="tableState.page"
            :items-per-page.sync="tableState.itemsPerPage"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
            class="custom-table"
          >
            <template #header.unitsMissingDevices>
              <span
                style="
                  display: flex;
                  align-items: center;
                  justify-content: center;
                "
              >
                Assign Tenant Device
                <v-icon
                  color="#d32f2f"
                  size="20"
                  class="ml-1"
                  aria-label="Warning"
                >
                  mdi-alert
                </v-icon>
              </span>
            </template>
            <template #item="{ item }">
              <tr>
                <td style="min-width: 200px">{{ item.facilityName }}</td>
                <td style="min-width: 120px; text-align: center">
                  {{ item.totalDevices }}
                </td>
                <td style="min-width: 150px; text-align: center">
                  <a
                    v-if="item.preInstalledUnits > 0"
                    class="inventory-link"
                    style="
                      cursor: pointer;
                      color: #1976d2;
                      text-decoration: underline;
                    "
                    @click="navigateToInventory(item.facilityId, 1)"
                  >
                    {{ item.preInstalledUnits }}
                  </a>
                  <span v-else>{{ item.preInstalledUnits }}</span>
                </td>
                <td style="min-width: 120px; text-align: center">
                  <a
                    v-if="item.officeInventory > 0"
                    class="inventory-link"
                    style="
                      cursor: pointer;
                      color: #1976d2;
                      text-decoration: underline;
                    "
                    @click="navigateToInventory(item.facilityId, 0)"
                  >
                    {{ item.officeInventory }}
                  </a>
                  <span v-else>{{ item.officeInventory }}</span>
                </td>
                <td style="min-width: 150px; text-align: center">
                  <a
                    v-if="item.unhealthyDevices > 0"
                    @click="navigateToInventory(item.facilityId, 2)"
                    class="inventory-link"
                    style="
                      cursor: pointer;
                      color: #1976d2;
                      text-decoration: underline;
                    "
                  >
                    {{ item.unhealthyDevices }}
                  </a>
                  <span v-else>{{ item.unhealthyDevices }}</span>
                </td>
                <td style="min-width: 120px; text-align: center">
                  <a
                    v-if="item.unitsMissingDevices > 0"
                    class="inventory-link"
                    style="
                      cursor: pointer;
                      color: #1976d2;
                      text-decoration: underline;
                    "
                    @click="navigateToUnitMissedDevices(item.facilityId)"
                  >
                    {{ item.unitsMissingDevices }}
                  </a>
                  <span v-else>{{ item.unitsMissingDevices }}</span>
                </td>
              </tr>
            </template>

            <template #footer.prepend>
              <ButtonExportCSV
                class="order-last"
                :table-data="inventoryData"
                :headers="headers"
                filename="Organization Inventory Report"
              />
              <ButtonPrint
                class="order-last"
                :before-print="showAllItems"
                :after-print="restoreItemsPerPage"
              />
            </template>
          </v-data-table>
        </v-skeleton-loader>
      </v-col>
    </v-row>
  </LayoutPage>
</template>

<script>
import RegionAndFacilitySelect from "@components/dashboard/RegionAndFacilitySelect.vue";
import LayoutPage from "@layout/LayoutPage.vue";
import useDashboard from "../dashboard/useDashboard";
import useFacility from "@components/authentication/useFacility";
import ButtonExportCSV from "@atoms/ButtonExportCSV.vue";
import ButtonPrint from "@atoms/ButtonPrint.vue";
import useDataTable from "../common/useDataTable";
import { ORGANIZATION_INVENTORY_SUMMARY } from "./graphql";

export default {
  name: "PageOrganizationInventory",
  components: {
    RegionAndFacilitySelect,
    LayoutPage,
    ButtonExportCSV,
    ButtonPrint,
  },
  setup() {
    // RegionAndFacilitySelect (always rendered below) owns loading the org set:
    // the accessible-scope union for facility/region/organization managers, the
    // single-root descendants for platform/application admins. This page used to
    // fetch the single root itself as well, and the two writes to the shared
    // useDashboard state raced, so a multi-facility manager could get a report
    // narrowed to their default org's subtree instead of everything they can see.
    const { organizationsLoading, selectedFacilities } = useDashboard();
    const { selectFacility } = useFacility();
    const { tableState, showAllItems, restoreItemsPerPage } = useDataTable({
      itemsPerPage: 10,
      sortBy: ["facilityName"],
      sortDesc: [false],
    });

    return {
      organizationsLoading,
      selectedFacilities,
      tableState,
      selectFacility,
      showAllItems,
      restoreItemsPerPage,
    };
  },
  data() {
    return {
      showRegionFacilitySelectBoxes: true,
      headers: [
        {
          text: "Facility Name",
          value: "facilityName",
          align: "start",
          width: "200px",
        },
        {
          text: "Total Devices",
          value: "totalDevices",
          align: "center",
          width: "120px",
        },
        {
          text: "Pre-Installed Inventory",
          value: "preInstalledUnits",
          align: "center",
          width: "170px",
        },
        {
          text: "Office Inventory",
          value: "officeInventory",
          align: "center",
          width: "150px",
        },
        {
          text: "Unavailable Devices",
          value: "unhealthyDevices",
          align: "center",
          width: "150px",
        },
        {
          text: "Assign Tenant Device",
          value: "unitsMissingDevices",
          align: "center",
          width: "120px",
        },
      ],
    };
  },
  computed: {
    loading() {
      const { organizationInventorySummary } = this.$apollo.queries;
      return organizationInventorySummary?.loading || this.organizationsLoading;
    },
    facilityIds() {
      const ids = this.selectedFacilities.map((facility) => facility.id);
      return ids;
    },
    inventoryData() {
      if (!this.organizationInventorySummary) return [];

      return this.organizationInventorySummary.map((item) => ({
        facilityId: item.facilityId || item.id,
        facilityName: item.facilityName || "--",
        totalDevices: item.totalDevices || 0,
        preInstalledUnits: item.preInstalledUnits || 0,
        officeInventory: item.officeInventory || 0,
        unhealthyDevices: item.unhealthyDevices || 0,
        unitsMissingDevices: item.unitsMissingDevices || 0,
      }));
    },
  },
  apollo: {
    organizationInventorySummary: {
      query: ORGANIZATION_INVENTORY_SUMMARY,
      variables() {
        const vars = {
          ids: this.facilityIds,
        };
        return vars;
      },
      skip() {
        const shouldSkip = !this.facilityIds.length;
        return shouldSkip;
      },
      update({ organizationInventorySummary }) {
        return organizationInventorySummary || [];
      },
    },
  },
  methods: {
    facilityFor(facilityId) {
      return this.selectedFacilities.find((f) => f.id === facilityId);
    },
    async navigateToInventory(facilityId, tab) {
      if (!facilityId) return;

      try {
        // Establish the facility context before routing. Fall back to the bare id
        // when the row's facility isn't in the current selection, since
        // selectFacility hydrates it from GET_FACILITY, so the facility page
        // never renders against a stale context.
        await this.selectFacility(
          this.$apollo,
          this.facilityFor(facilityId) || { id: facilityId }
        );

        let route = `/organizations/${facilityId}/inventory`;

        if (tab === 1) {
          route += "?t=1";
        } else if (tab === 2) {
          route += "?t=2";
        }

        await this.$router.push(route);
      } catch (error) {
        console.error("Error navigating to inventory:", error);
      }
    },
    async navigateToUnitMissedDevices(facilityId) {
      if (!facilityId) return;

      try {
        await this.selectFacility(
          this.$apollo,
          this.facilityFor(facilityId) || { id: facilityId }
        );

        // Navigate to the unit missed devices report
        const route = `/organizations/${facilityId}/unit-missed-devices`;
        await this.$router.push(route);
      } catch (error) {
        console.error("Error navigating to unit missed devices report:", error);
      }
    },
  },
};
</script>

<style scoped>
.custom-table .v-data-table__wrapper {
  overflow-x: auto;
}

.warning-label {
  color: #d32f2f;
  font-weight: bold;
  margin-left: 4px;
}

.inventory-link:hover {
  color: #1565c0 !important;
  text-decoration: underline !important;
}
</style>
